import { useEffect, useState } from 'react';
import { PopoverShell } from '@/components/PopoverShell';
import { useI18nText } from '@/lib/utils/i18nText';
import { CHANNEL_TIERS, tierToRange, type ChannelTier } from '@/lib/utils/channelTiers';

/** Formats a "min~max" range for display — the exact Mega/Macro/Micro range shows its tier
 *  label, anything else (a manually-typed custom range) shows the raw numbers. */
function formatRangeDisplay(value: string, t: (en: string, ko: string) => string): string {
  const tier = (Object.keys(CHANNEL_TIERS) as ChannelTier[]).find((k) => tierToRange(k) === value);
  if (tier) {
    const cfg = CHANNEL_TIERS[tier];
    return `${t(cfg.label, cfg.labelKo)} (${t(cfg.desc, cfg.descKo)})`;
  }
  const [min, max] = value.split('~');
  if (min && max) return `${min} ~ ${max}`;
  if (min) return `${min}+`;
  if (max) return `~${max}`;
  return value;
}

/**
 * Subscriber-count filter field — click opens a popover with Mega/Macro/Micro tier
 * shortcuts plus a manual min/max form, instead of requiring a hand-typed "min~max" string.
 */
export function SubscriberRangeInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');

  // Reseed the manual fields from the current value each time the popover opens.
  useEffect(() => {
    if (!open) return;
    const [from, to] = value.split('~');
    setMin(from ?? '');
    setMax(to ?? '');
  }, [open, value]);

  const applyTier = (tier: ChannelTier) => {
    onChange(tierToRange(tier));
    setOpen(false);
  };

  const applyManual = () => {
    const from = min.trim();
    const to = max.trim();
    onChange(from || to ? `${from}~${to}` : '');
    setOpen(false);
  };

  const clear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange('');
    setMin('');
    setMax('');
    setOpen(false);
  };

  const hasValue = !!value;

  return (
    <div className="filter-section--compact">
      <PopoverShell
        open={open}
        onOpenChange={setOpen}
        className="subscriber-range-popover"
        trigger={
          <button
            type="button"
            className={`filter-input-wrap subscriber-range-trigger${hasValue ? ' filter-input-wrap--has-value' : ''}`}
          >
            <span className={`subscriber-range-trigger__text${!hasValue ? ' subscriber-range-trigger__text--empty' : ''}`}>
              {hasValue ? formatRangeDisplay(value, t) : (placeholder ?? t('Enter subscribers', '구독자를 입력하세요'))}
            </span>
            {hasValue && (
              <span
                role="button"
                tabIndex={-1}
                className="filter-input-clear"
                onClick={clear}
                title={t('Clear', '지우기')}
              >
                <i className="fa fa-times-circle" />
              </span>
            )}
          </button>
        }
      >
        <div className="subscriber-range-popover__title">{t('Channel size', '채널 규모')}</div>
        <div className="subscriber-tier-list">
          {(Object.keys(CHANNEL_TIERS) as ChannelTier[]).map((tier) => {
            const cfg = CHANNEL_TIERS[tier];
            const isActive = value === tierToRange(tier);
            return (
              <button
                key={tier}
                type="button"
                onClick={() => applyTier(tier)}
                className={`subscriber-tier-btn${isActive ? ' subscriber-tier-btn--active' : ''}`}
              >
                <span className="subscriber-tier-btn__stars">
                  {Array.from({ length: cfg.stars }, (_, i) => <i key={i} className="fa-solid fa-star" />)}
                </span>
                <span className="subscriber-tier-btn__label">{t(cfg.label, cfg.labelKo)}</span>
                <span className="subscriber-tier-btn__desc">{t(cfg.desc, cfg.descKo)}</span>
              </button>
            );
          })}
        </div>
        <div className="subscriber-range-popover__divider">{t('Or enter a custom range', '또는 직접 범위 입력')}</div>
        <div className="subscriber-range-manual">
          <input
            type="number"
            min={0}
            value={min}
            onChange={(e) => setMin(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyManual(); }}
            placeholder={t('Min', '최소')}
            className="subscriber-range-manual__input"
          />
          <span className="subscriber-range-manual__sep">~</span>
          <input
            type="number"
            min={0}
            value={max}
            onChange={(e) => setMax(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyManual(); }}
            placeholder={t('Max', '최대')}
            className="subscriber-range-manual__input"
          />
        </div>
        <div className="subscriber-range-popover__actions">
          <button type="button" className="subscriber-range-popover__clear-btn" onClick={() => clear()}>
            {t('Clear', '지우기')}
          </button>
          <button type="button" className="subscriber-range-popover__apply-btn" onClick={applyManual}>
            {t('Apply', '적용')}
          </button>
        </div>
      </PopoverShell>
    </div>
  );
}
